import { useCallback, useState } from 'react';
import { environmentManager, secretManager } from 'services/api/config';
import { RecommendationsGroup } from './types';

const BASE_URL = secretManager.recommendationBackendUrl;

type RecommendationErrors = Array<{
  code?: number;
  message: string;
}>;

interface RecommendationResults {
  loading: boolean;
  error: RecommendationErrors | null;
  recommendations: RecommendationsGroup | null;
}

interface UseRecommendations {
  queryRecommendations: (query: string) => void;
  results: RecommendationResults;
}

const useRecommendations = (): UseRecommendations => {
  const [loading, setLoading] = useState(false);
  const [recommendations, setRecommendations] =
    useState<RecommendationsGroup | null>(null);
  const [error, setError] = useState<RecommendationErrors | null>(null);

  const queryRecommendations = useCallback(async (query) => {
    setLoading(true);
    try {
      const recommendationsResponse = await fetch(
        `${BASE_URL}/v2/recommendations?${new URLSearchParams({
          query: encodeURIComponent(query),
        })}&env=${environmentManager.currentEnv()}`
      );

      const recommendationsData =
        await (recommendationsResponse.json() as Promise<
          RecommendationsGroup | RecommendationErrors | { message: string }
        >);

      if (recommendationsResponse.ok) {
        setRecommendations(recommendationsData as RecommendationsGroup);
        setError(null);
      } else {
        setError(
          ([] as RecommendationErrors).concat(
            recommendationsData as RecommendationErrors | { message: string }
          )
        );
      }
    } catch (e) {
      setError([{ message: (e as Error).message }]);
    }
    setLoading(false);
  }, []);

  return {
    queryRecommendations,
    results: {
      recommendations,
      loading,
      error,
    },
  };
};

export default useRecommendations;
